import { useEffect, useRef, useState } from 'react'
import { Container, Card, Col, Row, Form, Spinner, Button, Modal } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import home_logo from '../../assets/icons/ICRI_NEW1.png';
import MockService from '../../Services/MockService';

const LABEL_STYLE = { fontSize: '0.75rem' };

export default function Onboarding() {

    const navigate = useNavigate();
    const [profile, setProfile] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState({
        name: '',
        last_name: '',
        gender: '',
        mobile: '',
        dob: '',
        city: '',
        image: '',
    });

    // Camera state
    const [showCamera, setShowCamera] = useState(false);
    const [cameraReady, setCameraReady] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const streamRef = useRef<MediaStream | null>(null);

    const fetchProfile = async () => {
        await MockService.getMockProfile()
            .then((res) => {
                if (res.status === 200) {
                    setProfile(res.data?.data?.user);
                }
            })
            .catch((err) => {
                console.error("Error fetching profile:", err);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchProfile();
    }, []);

    const onChange = (e: any) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    // Start camera when modal opens
    const openCamera = async () => {
        setShowCamera(true);
        setCameraReady(false);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.onloadedmetadata = () => setCameraReady(true);
            }
        } catch (err) {
            toast.error('Could not access camera');
            setShowCamera(false);
        }
    };

    // Stop camera stream
    const stopCamera = () => {
        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        setCameraReady(false);
    };

    const closeCamera = () => {
        stopCamera();
        setShowCamera(false);
    };

    // Capture frame → data URI → store in form.image
    const capture = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d')!.drawImage(video, 0, 0);
        const dataUri = canvas.toDataURL('image/jpeg', 0.85);

        setForm(prev => ({ ...prev, image: dataUri }));
        stopCamera();
        setShowCamera(false);
        toast.success('Photo captured');
    };

    const handleSubmit = async () => {
        setSaving(true);
        await MockService.mockProfileUpdate({ ...form, dob: form.dob ? new Date(form.dob).toISOString() : '' })
            .then((res) => {
                if (res.status === 200) {
                    toast.success('Profile updated successfully');
                    navigate('/exam/student-info');
                }
            })
            .catch((err) => {
                console.error(err);
                toast.error('Failed to update profile');
            })
            .finally(() => setSaving(false));
    };

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#eeeeee' }}>
            <Container style={{ maxWidth: '580px' }} className="pb-4">
                <div className="d-flex justify-content-center align-items-center flex-column pt-3 pb-2">
                    <img src={home_logo} height="70px" />
                    <h3 className="text-center font-weight-bold mt-2">Complete Your Profile</h3>
                    <p className="text-center text-muted">Fill in your details to continue</p>
                </div>

                {/* Read-only info card */}
                <Card className="p-3 border-0 shadow-sm mb-3">
                    {loading ? (
                        <div className="d-flex justify-content-center py-3">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : (
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-2">
                                    <Form.Label className="text-muted fw-semibold" style={LABEL_STYLE}>EMAIL</Form.Label>
                                    <Form.Control readOnly value={profile?.email || '—'} size="sm" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-2">
                                    <Form.Label className="text-muted fw-semibold" style={LABEL_STYLE}>REGISTRATION NUMBER</Form.Label>
                                    <Form.Control readOnly value={profile?.registrationNumber || '—'} size="sm" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-2">
                                    <Form.Label className="text-muted fw-semibold" style={LABEL_STYLE}>CENTER</Form.Label>
                                    <Form.Control readOnly value={profile?.center_name || '—'} size="sm" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-2">
                                    <Form.Label className="text-muted fw-semibold" style={LABEL_STYLE}>USERNAME</Form.Label>
                                    <Form.Control readOnly value={profile?.username || '—'} size="sm" />
                                </Form.Group>
                            </Col>
                        </Row>
                    )}
                </Card>

                {/* Editable update form */}
                <Card className="p-4 border-0 shadow">
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="text-muted fw-semibold" style={LABEL_STYLE}>FIRST NAME</Form.Label>
                                    <Form.Control type="text" name="name" placeholder="First name" value={form.name} onChange={onChange} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="text-muted fw-semibold" style={LABEL_STYLE}>LAST NAME</Form.Label>
                                    <Form.Control type="text" name="last_name" placeholder="Last name" value={form.last_name} onChange={onChange} />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="text-muted fw-semibold" style={LABEL_STYLE}>GENDER</Form.Label>
                                    <Form.Select name="gender" value={form.gender} onChange={onChange}>
                                        <option value="">Select gender</option>
                                        <option value="male">Male</option>
                                        <option value="female">Female</option>
                                        <option value="other">Other</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="text-muted fw-semibold" style={LABEL_STYLE}>MOBILE</Form.Label>
                                    <Form.Control type="text" name="mobile" placeholder="Mobile number" value={form.mobile} onChange={onChange} />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="text-muted fw-semibold" style={LABEL_STYLE}>DATE OF BIRTH</Form.Label>
                                    <Form.Control type="date" name="dob" value={form.dob} onChange={onChange} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="text-muted fw-semibold" style={LABEL_STYLE}>CITY</Form.Label>
                                    <Form.Control type="text" name="city" placeholder="City" value={form.city} onChange={onChange} />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Photo capture field */}
                        <Form.Group className="mb-3">
                            <Form.Label className="text-muted fw-semibold" style={LABEL_STYLE}>PHOTO</Form.Label>
                            <div className="d-flex align-items-center gap-2">
                                {form.image ? (
                                    <>
                                        <img
                                            src={form.image}
                                            alt="captured"
                                            style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid #dee2e6' }}
                                        />
                                        <Button variant="outline-secondary" size="sm" onClick={openCamera}>
                                            Retake
                                        </Button>
                                    </>
                                ) : (
                                    <Button variant="outline-primary" onClick={openCamera}>
                                        Open Camera
                                    </Button>
                                )}
                            </div>
                        </Form.Group>

                        <Button
                            variant="primary"
                            size="lg"
                            className="w-100 mt-2"
                            onClick={handleSubmit}
                            disabled={saving}
                        >
                            <span className="xcn-text-14">
                                {saving ? 'Saving...' : 'Save & Continue'}
                            </span>
                        </Button>
                    </Form>
                </Card>
            </Container>

            {/* Camera Modal */}
            <Modal show={showCamera} onHide={closeCamera} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '1rem' }}>Capture Photo</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0" style={{ background: '#000' }}>
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        style={{ width: '100%', display: 'block' }}
                    />
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeCamera}>Cancel</Button>
                    <Button variant="primary" onClick={capture} disabled={!cameraReady}>
                        Capture
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
